import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { keyframes } from '@mui/system';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Fade,
  Grow,
} from '@mui/material';
import {
  CloudQueue as CloudIcon,
  Article as ArticleIcon,
} from '@mui/icons-material';

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const pulsate = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 4px 20px rgba(25, 118, 210, 0.5),
                0 0 30px rgba(25, 118, 210, 0.3);
  }
  50% {
    transform: scale(1.08);
    box-shadow: 0 8px 30px rgba(25, 118, 210, 0.8),
                0 0 50px rgba(25, 118, 210, 0.6),
                inset 0 0 20px rgba(255, 255, 255, 0.3);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 4px 20px rgba(25, 118, 210, 0.5),
                0 0 30px rgba(25, 118, 210, 0.3);
  }
`;

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const Blog: React.FC = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('blog');
    if (element) {
      observer.observe(element);
    }

    return () => observer.disconnect();
  }, []);
  
  const blogPosts = [
    {
      id: 1,
      slug: 'ai-transformation-business-infrastructure-2026',
      title: 'How AI is Transforming Business Infrastructure in 2026',
      excerpt: 'Explore how artificial intelligence is reshaping IT infrastructure management, automation, and decision-making in modern enterprises.',
      category: 'Technology Trends',
      readTime: '7 min read',
      date: 'February 15, 2026',
      keywords: ['artificial intelligence', 'AI infrastructure', 'automation', 'digital transformation']
    },
    {
      id: 2,
      slug: 'data-sovereignty-regulations-2026',
      title: 'Data Sovereignty: Navigating Global Regulations in 2026',
      excerpt: 'Understanding how new data residency laws and regulations are impacting infrastructure decisions for global enterprises.',
      category: 'Compliance',
      readTime: '8 min read',
      date: 'February 10, 2026',
      keywords: ['data sovereignty', 'GDPR', 'compliance', 'data residency']
    },
    {
      id: 3,
      slug: 'hybrid-work-infrastructure-challenges-2026',
      title: 'Infrastructure Challenges in the Hybrid Work Era',
      excerpt: 'Learn how organizations are adapting their IT infrastructure to support permanent hybrid work models effectively.',
      category: 'Business Continuity',
      readTime: '6 min read',
      date: 'February 5, 2026',
      keywords: ['hybrid work', 'remote infrastructure', 'digital workplace', 'collaboration']
    },
    {
      id: 4,
      slug: 'sustainability-green-data-centers-2026',
      title: 'The Rise of Green Data Centers: Sustainability in IT',
      excerpt: 'Discover how businesses are reducing carbon footprint through sustainable infrastructure practices and energy-efficient technologies.',
      category: 'Sustainability',
      readTime: '5 min read',
      date: 'January 28, 2026',
      keywords: ['green IT', 'sustainability', 'energy efficiency', 'carbon neutral']
    },
    {
      id: 5,
      slug: 'quantum-computing-impact-enterprise-2026',
      title: 'Quantum Computing: Preparing Your Infrastructure for the Next Leap',
      excerpt: 'Understanding the implications of quantum computing and how enterprises should prepare their infrastructure for this revolution.',
      category: 'Innovation',
      readTime: '6 min read',
      date: 'January 20, 2026',
      keywords: ['quantum computing', 'future technology', 'infrastructure planning', 'innovation']
    },
    {
      id: 6,
      slug: 'zero-trust-architecture-adoption-2026',
      title: 'Zero Trust Architecture: Why Every Business Needs It Now',
      excerpt: 'Explore the shift towards zero trust security models and how organizations are implementing this critical infrastructure approach.',
      category: 'Security',
      readTime: '7 min read',
      date: 'January 15, 2026',
      keywords: ['zero trust', 'security architecture', 'cyber defense', 'access control']
    }
  ];

  return (
    <Box id="blog" sx={{ py: { xs: 12, md: 16 }, backgroundColor: 'background.default' }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 10 }}>
          <Typography variant="h2" component="h2" gutterBottom>
            IT Infrastructure Insights & Best Practices
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto', fontSize: '1.25rem' }}>
            Stay informed with the latest trends in virtualization, cloud infrastructure, 
            disaster recovery, and data protection from TNCOSOFT's experts.
          </Typography>
        </Box>

        <Grid container spacing={5}>
          {blogPosts.map((post, index) => (
            <Grid item xs={12} md={6} lg={4} key={post.id}>
              <Grow
                in={true}
                timeout={1000 + index * 200}
                style={{ transformOrigin: '0 0 0' }}
              >
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    animation: isVisible ? `${fadeInScale} 0.6s ease-out ${index * 0.1}s both, ${float} 3s ease-in-out ${index * 0.5}s infinite` : 'none',
                    '&:hover': {
                      transform: 'translateY(-12px) scale(1.03)',
                      animation: 'none',
                    },
                  }}
                  onClick={() => navigate(`/blog/${post.slug}`)}
                >
                <CardContent sx={{ flexGrow: 1, p: 4 }}>
                  <Chip 
                    label={post.category} 
                    sx={{ 
                      mb: 3,
                      bgcolor: 'primary.main',
                      color: 'white',
                      fontWeight: 600,
                      transition: 'all 0.3s ease',
                      '& .MuiChip-icon': { color: 'white' },
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                      },
                    }}
                    icon={<CloudIcon />}
                  />
                  
                  <Typography 
                    variant="h6" 
                    component="h3" 
                    gutterBottom
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                      mb: 2,
                      lineHeight: 1.3,
                    }}
                  >
                    {post.title}
                  </Typography>
                  
                  <Typography 
                    color="text.secondary" 
                    paragraph
                    sx={{
                      fontSize: '0.95rem',
                      lineHeight: 1.6,
                      mb: 3,
                    }}
                  >
                    {post.excerpt}
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, fontWeight: 500 }}>
                    <Typography variant="caption" color="text.secondary">
                      {post.date}
                    </Typography>
                    <Typography variant="caption" color="primary.main">
                      {post.readTime}
                    </Typography>
                  </Box>
                </CardContent>
                
                <CardActions sx={{ p: 4, pt: 0 }}>
                  <Button 
                    size="small" 
                    color="primary"
                    startIcon={<ArticleIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/blog/${post.slug}`);
                    }}
                  >
                    Read More
                  </Button>
                </CardActions>
                </Card>
              </Grow>
            </Grid>
          ))}
        </Grid>

        <Fade in={true} timeout={2000}>
          <Box sx={{ textAlign: 'center', mt: 12 }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            sx={{ 
              py: 2.5, 
              px: 6,
              fontSize: '1.1rem',
              fontWeight: 600,
              animation: `${pulsate} 2s ease-in-out infinite`,
              '&:hover': {
                animation: 'none',
                transform: 'scale(1.05)',
              }
            }}
          >
            Get Quote Now!
          </Button>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default Blog;